import { useCallback, useMemo } from 'react'
import { useCoachRosterState } from '../../api/useCoachRoster'
import { useParticipantIdentity } from '../../useParticipantName'
import { useCareTeamMembers } from '../careTeam/useCareTeamMembers'
import { useCareTeamTemplate } from '../careTeam/useCareTeamTemplate'
import { roleLabelFor } from '../careTeam/roleCategories'
import { RoleCategoryId, groupMembersByCategory, labelForMember } from '../careTeam/types'
import { CareTeamRosterEntry } from './types'
import { isActiveCareTeamMember } from '../careTeam/activeMember'
import { canonicalCareTeamRole, useAdvancedSettings } from '../../api/AdvancedSettingsProvider'

const UNKNOWN_PROVIDER = 'Unknown provider'

export type CareTeamRosterState = {
  roster: CareTeamRosterEntry[]
  roleFor: (coachId: string) => string | null
  categoryFor: (coachId: string) => RoleCategoryId | null
  nameFor: (coachId: string) => string
}

// The template sets order; the first real member in a category fills it.
export const useCareTeamRoster = (uid: string, chatOnly = false): CareTeamRosterState => {
  const { settings } = useAdvancedSettings()
  const { programId } = useParticipantIdentity(uid)
  const { template } = useCareTeamTemplate(programId ?? '')
  const { members } = useCareTeamMembers(uid)
  const { coaches, loading } = useCoachRosterState()
  // Blank while the coach list loads, so a provider isn't briefly shown as unknown.
  const fallbackName = loading ? '' : UNKNOWN_PROVIDER

  const roster = useMemo(() => {
    const coachName = (coachId: string) => coaches.find((coach) => coach.id === coachId)?.name ?? fallbackName
    const seen = new Set<string>()
    const entries: CareTeamRosterEntry[] = []
    const activeMembers = members.filter(isActiveCareTeamMember)
    const excluded = new Set(
      activeMembers
        .filter(
          (member) => !settings || settings.careTeamChatExcludedRoles.includes(canonicalCareTeamRole(member.role))
        )
        .map((member) => member.providerUid)
    )
    const visibleMembers = chatOnly
      ? activeMembers.filter((member) => !excluded.has(member.providerUid))
      : activeMembers

    groupMembersByCategory(template.categoryIds, visibleMembers).forEach(({ categoryId, members: inCategory }) => {
      const categoryLabel = roleLabelFor(categoryId)

      inCategory.forEach((member, index) => {
        if (seen.has(member.providerUid)) {
          return
        }
        seen.add(member.providerUid)
        entries.push({
          id: member.providerUid,
          name: coachName(member.providerUid),
          role: labelForMember(categoryLabel, index),
          categoryId,
        })
      })
    })

    return entries
  }, [template, members, coaches, fallbackName, chatOnly, settings])

  const rosterById = useMemo(() => new Map(roster.map((member) => [member.id, member])), [roster])

  const roleFor = useCallback((coachId: string) => rosterById.get(coachId)?.role ?? null, [rosterById])

  const categoryFor = useCallback((coachId: string) => rosterById.get(coachId)?.categoryId ?? null, [rosterById])

  const nameFor = useMemo(() => {
    const byId = new Map(coaches.map((coach) => [coach.id, coach.name]))

    return (coachId: string) => byId.get(coachId) ?? fallbackName
  }, [coaches, fallbackName])

  return { roster, roleFor, categoryFor, nameFor }
}
